import { useCallback, useEffect, useState } from "react";
import {
  acceptAllConsent,
  getInitialConsentPreferences,
  persistAndApplyConsentPreferences,
  rejectOptionalConsent,
} from "@/lib/consent/consentManager";
import type { ConsentPreferences } from "@/lib/consent/consentTypes";

export function useCookieConsent() {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(() => getInitialConsentPreferences());
  const [showBanner, setShowBanner] = useState(() => getInitialConsentPreferences() === null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    const handleSettingsRequest = () => {
      setShowBanner(false);
      setShowDialog(true);
    };
    const handleConsentChange = (event: Event) => {
      const detail = (event as CustomEvent<ConsentPreferences>).detail;
      setPreferences(detail);
      setShowBanner(false);
    };
    window.addEventListener("justicedrive:open-cookie-settings", handleSettingsRequest);
    window.addEventListener("justicedrive:cookie-consent-changed", handleConsentChange);
    return () => {
      window.removeEventListener("justicedrive:open-cookie-settings", handleSettingsRequest);
      window.removeEventListener("justicedrive:cookie-consent-changed", handleConsentChange);
    };
  }, []);

  const acceptAll = useCallback(() => {
    setPreferences(acceptAllConsent());
    setShowBanner(false);
    setShowDialog(false);
  }, []);

  const rejectOptional = useCallback(() => {
    setPreferences(rejectOptionalConsent());
    setShowBanner(false);
    setShowDialog(false);
  }, []);

  const saveChoices = useCallback((values: Pick<ConsentPreferences, "analytics" | "advertising">) => {
    setPreferences(persistAndApplyConsentPreferences(values));
    setShowBanner(false);
    setShowDialog(false);
  }, []);

  return {
    preferences,
    showBanner,
    showDialog,
    openDialog: () => setShowDialog(true),
    closeDialog: () => setShowDialog(false),
    acceptAll,
    rejectOptional,
    saveChoices,
  };
}
